import { useSearch } from '@elastic/react-search-ui'
import { SearchBoxAutocompleteViewProps } from '@elastic/react-search-ui-views'
import { AutocompletedSuggestion, SearchContextState } from '@elastic/search-ui'
import { SearchResultInterface } from 'common/types/search-interfaces'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { CommonSearchProps } from 'src/shared/types/entities/search-interface'
import { SearchItem } from '../search-item'
import { SearchAutocompleteWrapper } from './search-autocomplete-wrapper'
import {
  SearchAutocompleteDropdownUi,
  SearchAutocompleteSeeMoreUi,
  SearchAutocompleteSeeMoreWrapperUi,
  SearchAutocompleteWrapperUi,
} from './ui'
import { SearchAutocompleteErrorUi } from './ui/search-autocomplete-error-ui'
import { SearchAutocompleteLoadingUi } from './ui/search-autocomplete-loading-ui'
import { SearchAutocompleteLoadingWrapperUi } from './ui/search-autocomplete-loading-wrapper-ui'
import { SearchAutocompleteNoResultsUi } from './ui/search-autocomplete-no-results-ui'

interface AutocompleteSourceInterface {
  data: SearchResultInterface[]
  total: number
}
interface SearchAutocompleteViewProps
  extends Pick<
      SearchBoxAutocompleteViewProps,
      'autocompleteResults' | 'getItemProps' | 'getMenuProps'
    >,
    Omit<CommonSearchProps, 'padding' | 'mobilePadding' | 'input'> {
  autocompletedResults?: AutocompleteSourceInterface[]
  minChars?: number
  isPreviewMode: boolean
  forceOpenLoading?: boolean
}

export const SearchAutocompleteView = ({
  autocompletedResults,
  getItemProps,
  getMenuProps,
  item,
  itemTitle,
  itemText,
  seeMore,
  dropdown,
  minChars = 3,
  isPreviewMode,
}: SearchAutocompleteViewProps) => {
  const { t } = useTranslation()
  const source = autocompletedResults?.[0]
  const items: SearchResultInterface[] = source?.data ?? []
  const total = source?.total ?? 0
  const { error } = useSearch((s: SearchContextState) => ({
    error: s?.error,
  }))

  return (
    <SearchAutocompleteWrapper minChars={minChars}>
      {({ searchTerm, isLoading }) => {
        const hasQuery = (searchTerm ?? '').length >= minChars
        const shouldOpen =
          !!error || isLoading || (source && items.length > 0) || hasQuery

        return (
          <SearchAutocompleteWrapperUi>
            {shouldOpen && (
              <SearchAutocompleteDropdownUi
                {...dropdown}
                {...(getMenuProps ? getMenuProps({ className: '' }) : {})}
              >
                {!!error && (
                  <SearchAutocompleteErrorUi>
                    {String(error)}
                  </SearchAutocompleteErrorUi>
                )}

                {isLoading && !error && (
                  <SearchAutocompleteLoadingWrapperUi>
                    <SearchAutocompleteLoadingUi />
                  </SearchAutocompleteLoadingWrapperUi>
                )}

                {!error && !isLoading && hasQuery && items.length === 0 && (
                  <SearchAutocompleteNoResultsUi>
                    {t('components.search.no_results')} '{searchTerm}'
                  </SearchAutocompleteNoResultsUi>
                )}

                {!error &&
                  !isLoading &&
                  items.length > 0 &&
                  items.map((result: SearchResultInterface, i: number) => {
                    const suggestionItem: AutocompletedSuggestion = {
                      suggestion: result.name,
                      data: result,
                      queryType: 'suggestion',
                    }

                    return (
                      <SearchItem
                        {...(getItemProps
                          ? getItemProps({
                              key: String(result.id),
                              item: suggestionItem,
                              index: i,
                            })
                          : {})}
                        key={result.id}
                        title={result.name}
                        text={
                          result.content ? result.content : result.description
                        }
                        image={result.image}
                        path={result.path}
                        itemProps={item}
                        itemTitleProps={itemTitle}
                        itemTextProps={itemText}
                        isPreviewMode={isPreviewMode}
                      />
                    )
                  })}

                {!error && !isLoading && items.length > 0 && total > 3 && (
                  <SearchAutocompleteSeeMoreWrapperUi
                    padding={seeMore.padding}
                    mobilePadding={seeMore.mobilePadding}
                    alignSelf={seeMore.alignSelf}
                    mobileAlignSelf={seeMore.mobileAlignSelf}
                  >
                    <SearchAutocompleteSeeMoreUi {...seeMore} type="submit">
                      {seeMore.text}
                    </SearchAutocompleteSeeMoreUi>
                  </SearchAutocompleteSeeMoreWrapperUi>
                )}
              </SearchAutocompleteDropdownUi>
            )}
          </SearchAutocompleteWrapperUi>
        )
      }}
    </SearchAutocompleteWrapper>
  )
}
